// apps/web/src/features/agent/AgentPage.tsx
//
// Leadership "Agent" page (#1876). Mounts under `/dashboards/agent` behind
// `dashboard.read`; the route gate (`PermissionGate`) is authoritative, so
// this component does NOT re-check the permission.
//
// Responsive split-pane workspace: chat (composer + streamed answer + step
// progress + verdict) in the middle, artifacts on the right, and conversation
// history on the left. Below the `useNarrow` breakpoint the panes stack.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import type { AgentRenderableArtifact, AgentV2ConversationDetail, Artifact } from '@magimp/shared';
import { Button, Divider, useNarrow } from '../../ui/index.js';
import { hasPermission, useAuthStore } from '../../stores/auth.js';
import { useToastStore } from '../../stores/toast.js';
import { getAgentV2Conversation, pinAgentSavedDashboardItem } from '../../api/agent/index.js';
import { useAgentChat } from './useAgentChat.js';
import { AgentChatPanel } from './AgentChatPanel.js';
import { AgentComposer } from './AgentComposer.js';
import { AgentArtifactsPane } from './AgentArtifactsPane.js';
import { AgentAdminPanel } from './AgentAdminPanel.js';
import { AgentConversationHistory } from './AgentConversationHistory.js';

export function AgentPage() {
  const { t } = useTranslation('agent');
  const narrow = useNarrow();
  const permissions = useAuthStore((s) => s.permissions);
  const canAdmin = hasPermission(permissions, 'agent.admin');
  const showToast = useToastStore((s) => s.show);
  const [showAdmin, setShowAdmin] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [pinningArtifactId, setPinningArtifactId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const loadedUrlConversationIdRef = useRef<string | null>(null);

  const syncConversationId = useCallback(
    (id: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === undefined) next.delete('conversationId');
          else next.set('conversationId', id);
          return next;
        },
        { replace: true },
      );
      if (id !== undefined) setHistoryRefreshKey((value) => value + 1);
    },
    [setSearchParams],
  );

  const {
    conversationId,
    turns,
    streaming,
    step,
    artifacts,
    verdict,
    error,
    submit,
    loadConversation,
    newConversation,
  } = useAgentChat({ onConversationIdChange: syncConversationId });

  const handleSelectConversation = useCallback(
    (conversation: AgentV2ConversationDetail) => {
      loadedUrlConversationIdRef.current = conversation.id;
      loadConversation(conversation);
    },
    [loadConversation],
  );

  const urlConversationId = searchParams.get('conversationId');
  useEffect(() => {
    if (
      urlConversationId === null ||
      urlConversationId === conversationId ||
      loadedUrlConversationIdRef.current === urlConversationId
    )
      return;
    let cancelled = false;
    loadedUrlConversationIdRef.current = urlConversationId;
    getAgentV2Conversation(urlConversationId)
      .then((conversation) => {
        if (!cancelled) loadConversation(conversation);
      })
      .catch(() => {
        if (!cancelled) syncConversationId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, loadConversation, syncConversationId, urlConversationId]);

  const pinArtifact = useCallback(
    async (artifact: Artifact) => {
      setPinningArtifactId(artifact.id);
      try {
        await pinAgentSavedDashboardItem({
          artifact,
          sourceConversationId: conversationId ?? null,
          sourceMessageId: null,
        });
        showToast({ tone: 'success', text: t('artifacts.pinned') });
      } catch {
        showToast({ tone: 'error', text: t('artifacts.pinFailed') });
      } finally {
        setPinningArtifactId((current) => (current === artifact.id ? null : current));
      }
    },
    [conversationId, showToast, t],
  );

  const renderArtifactAction = useCallback(
    (artifact: AgentRenderableArtifact) => {
      if (artifact.kind === 'unsupported') return null;
      const loading = pinningArtifactId === artifact.id;
      return (
        <Button
          kind="ghost"
          size="sm"
          icon="save"
          disabled={loading}
          onClick={() => void pinArtifact(artifact)}
          data-testid="agent-pin-artifact"
        >
          {loading ? t('artifacts.pinning') : t('artifacts.pin')}
        </Button>
      );
    },
    [pinArtifact, pinningArtifactId, t],
  );

  const canReset = turns.length > 0 || streaming || conversationId !== undefined;

  return (
    <div
      data-testid="agent-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-4)',
        minHeight: 0,
        flex: 1,
        padding: 'var(--s-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          gap: 'var(--s-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: '1 1 auto' }}>{t('title')}</h1>
        {canAdmin && (
          <Button
            kind="secondary"
            icon="settings"
            active={showAdmin}
            onClick={() => setShowAdmin((value) => !value)}
            data-testid="agent-admin-toggle"
          >
            {t('admin.title')}
          </Button>
        )}
        <Button
          kind="secondary"
          icon="plus"
          disabled={!canReset}
          onClick={newConversation}
          data-testid="agent-new-conversation"
        >
          {t('newConversation')}
        </Button>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '240px minmax(0, 1fr) minmax(0, 1fr)',
          gap: 'var(--s-4)',
          minHeight: 0,
          flex: 1,
        }}
      >
        <AgentConversationHistory
          selectedConversationId={conversationId}
          refreshKey={historyRefreshKey}
          onSelect={handleSelectConversation}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--s-3)',
            minHeight: 0,
          }}
        >
          <AgentChatPanel
            turns={turns}
            streaming={streaming}
            step={step}
            verdict={verdict}
            error={error}
          />
          <div style={{ marginTop: 'auto' }}>
            <Divider style={{ marginBottom: 'var(--s-3)' }} />
            <AgentComposer disabled={streaming} onSubmit={submit} />
          </div>
        </div>

        <AgentArtifactsPane artifacts={artifacts} renderArtifactAction={renderArtifactAction} />
      </div>
      {canAdmin && showAdmin && <AgentAdminPanel />}
    </div>
  );
}
